import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useStaffFacilities, useStaffParticipants, useValidateChatParticipant, useCreateChatroom } from "../../features/chat";
import type { StaffParticipant } from "../../features/chat/types";
import { useBoundStore } from "../../store";
import { buildCreateChatStepsModel } from "./constants";

export interface CreateChatViewModelState {
  step: number;
  facility: string | null;
  userSearch: string;
  selectedSsoIds: string[];
  selectedParticipants: StaffParticipant[];
  groupName: string;
  participants: StaffParticipant[];
  participantsLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextParticipants: boolean;
  facilityChangeConfirmVisible: boolean;
  isCreating: boolean;
  createError: string | null;
  isValidating: boolean;
  rejectedDialogVisible: boolean;
  rejectedParticipants: StaffParticipant[];
  canProceedWithOthers: boolean;
  stepsModel: ReturnType<typeof buildCreateChatStepsModel>;
  canGoNext: boolean;
  canCreate: boolean;
  facilities: { label: string; value: string }[];
  facilitiesLoading: boolean;
  onFacilityChange: (value: string) => void;
  onConfirmFacilityChange: () => void;
  onCancelFacilityChange: () => void;
  onUserSearchChange: (query: string) => void;
  onToggleSsoId: (ssoId: string) => void;
  onGroupNameChange: (name: string) => void;
  onLoadMoreParticipants: () => void;
  onNext: () => void;
  onProceedWithOthers: () => void;
  onCancelRejected: () => void;
  onBack: () => void;
  onCancel: () => void;
  onCreate: () => void;
}

export function useCreateChatViewModel(): CreateChatViewModelState {
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [facilityOverride, setFacilityOverride] = useState<string | null>(null);
  const [pendingFacilityChange, setPendingFacilityChange] = useState<string | null>(null);
  const [userSearch, setUserSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedSsoIds, setSelectedSsoIds] = useState<string[]>([]);
  // Preserves full participant details across pagination pages for the step-2 summary
  const [selectedParticipantsMap, setSelectedParticipantsMap] = useState<Record<string, StaffParticipant>>({});
  const [groupName, setGroupName] = useState("");
  const [rejectedSsoIds, setRejectedSsoIds] = useState<string[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const { mutateAsync: validateParticipants } = useValidateChatParticipant();
  const { mutateAsync: createChatroom } = useCreateChatroom();

  const { data: facilitiesData, isLoading: facilitiesLoading } = useStaffFacilities();

  const autoSelectedFacility = useMemo(() => {
    const first = facilitiesData?.[0];
    return first ? String(first.facilityId) : null;
  }, [facilitiesData]);

  const facility = facilityOverride ?? autoSelectedFacility;

  // The GUID (UUID) matching the selected numeric facilityId — used as the API param
  const facilityGuid = useMemo(() => {
    if (!facilitiesData) return "";
    const match = facilitiesData.find((f) => String(f.facilityId) === facility);
    return match?.facilityGlobalId ?? "";
  }, [facility, facilitiesData]);

  const facilities = useMemo(
    () => (facilitiesData ?? []).map((f) => ({ label: f.facilityName, value: String(f.facilityId) })),
    [facilitiesData],
  );

  // Debounce search so the API is not called on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(userSearch), 400);
    return () => clearTimeout(timer);
  }, [userSearch]);

  const {
    data: participantsPages,
    isLoading: participantsLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useStaffParticipants(
    { facilityGuid, q: debouncedSearch || undefined },
    !!facilityGuid,
  );

  const participants = useMemo(
    () => (participantsPages?.pages ?? []).flatMap((p) => p?.data ?? []),
    [participantsPages],
  );

  const selectedParticipants = useMemo(
    () => selectedSsoIds.map((id) => selectedParticipantsMap[id]).filter((p): p is StaffParticipant => !!p),
    [selectedSsoIds, selectedParticipantsMap],
  );

  const handleToggleSsoId = useCallback((ssoId: string) => {
    setSelectedSsoIds((prev) =>
      prev.includes(ssoId) ? prev.filter((x) => x !== ssoId) : [...prev, ssoId],
    );
    setSelectedParticipantsMap((prev) => {
      if (prev[ssoId]) {
        return Object.fromEntries(Object.entries(prev).filter(([k]) => k !== ssoId)) as Record<string, StaffParticipant>;
      }
      const found = participants.find((p) => p.ssoId === ssoId);
      return found ? { ...prev, [ssoId]: found } : prev;
    });
  }, [participants]);

  const handleCancel = useCallback(() => navigate(-1), [navigate]);

  const handleCreate = useCallback(() => {
    if (!facilityGuid) return;
    setIsCreating(true);
    setCreateError(null);
    createChatroom({
      chatWith: "staff",
      data: {
        staffExternalId: selectedSsoIds,
        externalSiteId: facilityGuid,
        message: groupName.trim(),
      },
    })
      .then((result) => {
        useBoundStore.getState().setPendingNewChatroomId(result.roomId);
        navigate(-1);
      })
      .catch(() => setCreateError("Failed to create chat. Please try again."))
      .finally(() => setIsCreating(false));
  }, [selectedSsoIds, facilityGuid, groupName, createChatroom, navigate]);

  const handleNext = useCallback(() => {
    setIsValidating(true);
    validateParticipants({ externalUserIds: selectedSsoIds })
      .then((result) => {
        if (result.rejectedUsers?.length) {
          setRejectedSsoIds(result.rejectedUsers);
        } else {
          setStep(1);
        }
      })
      .catch(() => setStep(1))
      .finally(() => setIsValidating(false));
  }, [selectedSsoIds, validateParticipants]);

  const onLoadMoreParticipants = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const rejectedParticipants = useMemo(
    () => rejectedSsoIds.map(
      (ssoId) => selectedParticipantsMap[ssoId] ?? participants.find((p) => p.ssoId === ssoId) ?? { ssoId, displayName: ssoId, role: "" },
    ),
    [rejectedSsoIds, selectedParticipantsMap, participants],
  );

  return {
    step,
    facility,
    userSearch,
    selectedSsoIds,
    selectedParticipants,
    groupName,
    participants,
    participantsLoading,
    isFetchingNextPage,
    hasNextParticipants: !!hasNextPage,
    facilityChangeConfirmVisible: pendingFacilityChange !== null,
    isValidating,
    isCreating,
    createError,
    rejectedDialogVisible: rejectedSsoIds.length > 0,
    rejectedParticipants,
    canProceedWithOthers: selectedSsoIds.length > rejectedSsoIds.length,
    stepsModel: buildCreateChatStepsModel(step),
    canGoNext: selectedSsoIds.length > 0,
    canCreate: !!groupName.trim(),
    facilities,
    facilitiesLoading,
    onFacilityChange: (value: string) => {
      if (selectedSsoIds.length > 0) {
        setPendingFacilityChange(value);
      } else {
        setFacilityOverride(value);
      }
    },
    onConfirmFacilityChange: () => {
      setFacilityOverride(pendingFacilityChange);
      setSelectedSsoIds([]);
      setSelectedParticipantsMap({});
      setPendingFacilityChange(null);
    },
    onCancelFacilityChange: () => setPendingFacilityChange(null),
    onUserSearchChange: setUserSearch,
    onToggleSsoId: handleToggleSsoId,
    onGroupNameChange: setGroupName,
    onLoadMoreParticipants,
    onNext: handleNext,
    onProceedWithOthers: useCallback(() => {
      setSelectedSsoIds((prev) => prev.filter((id) => !rejectedSsoIds.includes(id)));
      setSelectedParticipantsMap((prev) => {
        const next = { ...prev };
        rejectedSsoIds.forEach((id) => delete next[id]);
        return next;
      });
      setRejectedSsoIds([]);
      setStep(1);
    }, [rejectedSsoIds]),
    onCancelRejected: () => setRejectedSsoIds([]),
    onBack: () => setStep(0),
    onCancel: handleCancel,
    onCreate: handleCreate,
  };
}
